import {
  Box,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import api from '../../../services/api/base';
import { getDisplayNames, saveDisplayNameReviewInternalNote, saveDisplayNameReviewStatus } from '../../../services/api/Moderation';
import {
  DisplayNameReviewStatus,
  IDisplayName,
  IDisplayNameRequestParamDto,
  IDisplayNameReview,
  ISuccessResult,
  ModerationSearchType,
  ModerationType,
  ReviewSortOrder,
} from '../../../services/types';
import DateRangePicker from '../../../components/DateRangePicker';
import TablePaginationActions from '../common/PaginationAction';
import Search from '../common/Search';
import reducer, { DisplayNameActionTypes } from './DisplayName.reducer';
import useStyles from './DisplayName.styles';
import DisplayNameChangeHistory from './DisplayNameChangeHistory';
import InternalNote from './DisplayNameInternalNote';
import DisplayNameTableRow from './DisplayNameTableRow';

interface IProps {
  isShownReportedOnly: boolean;
}

const initialState: IDisplayNameRequestParamDto = {
  startDate: undefined,
  endDate: undefined,
  reviewStatus: DisplayNameReviewStatus.NotReviewed,
  userId: undefined,
  username: undefined,
  displayName: undefined,
};

const RowsPerPage = 50;

const DisplayName = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const { isShownReportedOnly } = props;

  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [displayNames, setDisplayNames] = useState<IDisplayName[]>([]);
  const [page, setPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [showChangeHistoryViewer, setShowChangeHistoryViewer] = useState<boolean>(false);
  const [showInternalNote, setShowInternalNote] = useState<boolean>(false);
  const [currentViewDisplayName, setCurrentViewDisplayName] = useState<IDisplayName>();
  const [currentSelectedDisplayNameIds, setCurrentSelectedDisplayNameIds] = useState<Set<number>>(new Set<number>());
  const [isPendingChangeReviewStatus, setIsPendingChangeReviewStatus] = useState<boolean>(false);

  const [state, dispatch] = useReducer(reducer, initialState);

  const snackbar = useSnackbar();
  const confirm = useConfirmDialog();

  const handleDateRangeSelect = useCallback((startDate: Date, endDate: Date) => {
    dispatch({ type: DisplayNameActionTypes.SetDate, payload: { startDate: startDate.toISOString(), endDate: endDate.toISOString() } });

    setPage(0);
  }, []);

  const handleReviewStatusFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const reviewStatus = e.target.value as DisplayNameReviewStatus;
    dispatch({ type: DisplayNameActionTypes.SetReviewStatus, payload: { reviewStatus } });

    setPage(0);
  }, []);

  const handleSearchChange = useCallback((searchType: ModerationSearchType, query: string) => {
    if (searchType === ModerationSearchType.DisplayName) {
      dispatch({ type: DisplayNameActionTypes.SetDisplayName, payload: { displayName: query } });
    }

    if (searchType === ModerationSearchType.UserId) {
      dispatch({ type: DisplayNameActionTypes.SetUserId, payload: { userId: Number(query) } });
    }

    if (searchType === ModerationSearchType.UserName) {
      dispatch({ type: DisplayNameActionTypes.SetUserName, payload: { username: query } });
    }

    setPage(0);
  }, []);

  const fetchDisplayNames = useCallback(
    async (page: number, rowsPerPage: number, param: IDisplayNameRequestParamDto) => {
      const p = { ...param, isReported: isShownReportedOnly };

      if (!p.userId) {
        p.userId = undefined;
      }

      if (!p.username) {
        p.username = undefined;
      }

      if (!p.reviewStatus) {
        p.reviewStatus = undefined;
      }

      if (!p.displayName) {
        p.displayName = undefined;
      }

      setIsListLoading(true);

      const { data } = await getDisplayNames(p, page + 1, rowsPerPage);

      setDisplayNames(data.items);
      setTotalCount(data?.totalCount || 0);
      setIsListLoading(false);
      setCurrentSelectedDisplayNameIds(new Set<number>());
    },
    [isShownReportedOnly],
  );

  const handlePageChange = useCallback((e: unknown, page: number) => {
    setPage(page);
  }, []);

  const saveReviewStatus = useCallback(
    async (displayNameReviews: IDisplayNameReview[], reviewStatus: DisplayNameReviewStatus, onSuccess?: () => void) => {
      setIsPendingChangeReviewStatus(true);
      try {
        const result = await saveDisplayNameReviewStatus(
          displayNameReviews.map(r => r.id),
          reviewStatus,
        );

        const apiResult = result.data as ISuccessResult<IDisplayNameReview[]>;
        const { data: reviews } = apiResult;

        if (api.isResponseSuccess(result)) {
          const cloned = [...displayNames];

          reviews.forEach(r => {
            const displayName = cloned.find(d => d.id === r.userDisplayNameId);
            if (displayName) {
              displayName.review = r;
            }
          });

          setDisplayNames(cloned);
          if (onSuccess) {
            onSuccess();
          }
        } else {
          snackbar.error('The status cannot be saved.');
        }
      } catch (e) {
        snackbar.error(`The status cannot be saved. ${e.message}`);
      } finally {
        setIsPendingChangeReviewStatus(false);
      }
    },
    [displayNames, snackbar],
  );

  const handleReviewStatusOfEachItemChange = useCallback(
    (displayNameReview: IDisplayNameReview, reviewStatus: DisplayNameReviewStatus) => {
      const save = () => saveReviewStatus([displayNameReview], reviewStatus);
      if (reviewStatus === DisplayNameReviewStatus.Blocked) {
        confirm.open({
          title: `Block this display name?`,
          text: `Are you sure you want to block this display name?`,
          proceedButtonText: 'I am sure.',
          cancelButtonText: 'No',
          onProceed: save,
        });

        return;
      } else if (reviewStatus === DisplayNameReviewStatus.Approved) {
        const displayName = displayNames.find(d => d.id === displayNameReview.userDisplayNameId);

        if (displayName?.review.status === DisplayNameReviewStatus.Blocked) {
          confirm.open({
            title: `Approve this display name?`,
            text: 'This display name has been blocked. Are you sure you want to change its status to "Approved"?',
            proceedButtonText: 'I am sure.',
            cancelButtonText: 'No',
            onProceed: save,
          });

          return;
        }
      }

      save();
    },
    [saveReviewStatus, displayNames, confirm],
  );

  const handleChangeHistoryClick = useCallback((displayName: IDisplayName) => {
    setCurrentViewDisplayName(displayName);
    setShowChangeHistoryViewer(true);
    setShowInternalNote(false);
  }, []);

  const handleInternalNoteClick = useCallback((displayName: IDisplayName) => {
    setCurrentViewDisplayName(displayName);
    setShowInternalNote(true);
    setShowChangeHistoryViewer(false);
  }, []);

  const handleInternalNoteSave = useCallback((displayNameId: number, note: string) => {
    setDisplayNames(ps => {
      const cloned = [...ps];

      const displayName = cloned.find(d => d.id === displayNameId);
      if (displayName && displayName.review) {
        displayName.review.note = note;
      }
      return cloned;
    });
    setShowInternalNote(false);
  }, []);

  const handleInternalNoteClose = useCallback(() => {
    setShowInternalNote(false);
  }, []);

  const handleChangeHistoryViewerClose = useCallback(() => {
    setShowChangeHistoryViewer(false);
  }, []);

  const handleTableRowCheckChange = useCallback((displayName: IDisplayName) => {
    const { id } = displayName;

    setCurrentSelectedDisplayNameIds(ps => {
      const cloned = new Set(ps);

      if (cloned.has(id)) {
        cloned.delete(id);
      } else {
        cloned.add(id);
      }

      return cloned;
    });
  }, []);

  const handleSortOptionChange = useCallback((order: ReviewSortOrder) => {
    dispatch({ type: DisplayNameActionTypes.SetOrder, payload: { order } });
  }, []);

  const handleApproveButtonClick = useCallback(() => {
    saveReviewStatus(
      displayNames.filter(c => currentSelectedDisplayNameIds.has(c.id)).map(c => c.review),
      DisplayNameReviewStatus.Approved,
      () => setCurrentSelectedDisplayNameIds(new Set<number>()),
    );
  }, [displayNames, currentSelectedDisplayNameIds, saveReviewStatus]);

  const handleSelectAllCheckboxClick = useCallback(() => {
    setCurrentSelectedDisplayNameIds(ps => {
      const cloned = new Set(ps);

      if (cloned.size === displayNames.length) {
        cloned.clear();
      } else {
        displayNames.forEach(c => cloned.add(c.id));
      }

      return cloned;
    });
  }, [displayNames]);

  const ActionsComponent = useCallback(
    TablePaginationActions({
      order: state.order || ReviewSortOrder.Oldest,
      disabledButtons: isPendingChangeReviewStatus,
      selectedItemsCount: currentSelectedDisplayNameIds.size,
      itemsTotalCount: displayNames.length,
      disableReportedSortOption: isShownReportedOnly,
      onOrderChange: handleSortOptionChange,
      onApproveButtonClick: handleApproveButtonClick,
      onSelectAllCheckboxClick: handleSelectAllCheckboxClick,
    }),
    [
      isPendingChangeReviewStatus,
      currentSelectedDisplayNameIds,
      displayNames,
      handleSortOptionChange,
      handleApproveButtonClick,
      handleSelectAllCheckboxClick,
    ],
  );

  useEffect(() => {
    const { startDate, endDate } = state;

    if (!startDate || !endDate) {
      return;
    }

    fetchDisplayNames(page, RowsPerPage, state);
  }, [fetchDisplayNames, page, state]);

  const renderTablePagination = () => (
    <TablePagination
      ActionsComponent={ActionsComponent}
      count={totalCount}
      page={page}
      rowsPerPage={RowsPerPage}
      rowsPerPageOptions={[]}
      onChangePage={handlePageChange}
      labelDisplayedRows={() => null}
      classes={{
        toolbar: classes.paginationToolbar,
      }}
    />
  );

  return (
    <Box display="flex" flexDirection="column" paddingTop={'6px'}>
      <DateRangePicker startDate={state.startDate} endDate={state.endDate} onChange={handleDateRangeSelect} />
      <RadioGroup row value={state.reviewStatus} onChange={handleReviewStatusFilterChange} className={classes.radioButtonGroups}>
        <FormControlLabel value={''} control={<Radio color="primary" />} label="All Status" />
        <FormControlLabel value={DisplayNameReviewStatus.NotReviewed} control={<Radio color="primary" />} label="Not reviewed" />
        <FormControlLabel value={DisplayNameReviewStatus.Approved} control={<Radio color="primary" />} label="Approved" />
        <FormControlLabel value={DisplayNameReviewStatus.Pending} control={<Radio color="primary" />} label="Pending" />
        <FormControlLabel value={DisplayNameReviewStatus.Blocked} control={<Radio color="primary" />} label="Blocked" />
      </RadioGroup>
      <Search moderationType={ModerationType.Comments} onChange={handleSearchChange} />
      <Paper className={classes.tablePaperWrap}>
        {isListLoading && <Box className={classes.loadingWrap} />}
        {renderTablePagination()}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell padding="checkbox" className={classes.tableCell80}>
                {isShownReportedOnly ? 'Reported At' : 'Created At'}
              </TableCell>
              <TableCell padding="checkbox" className={classes.tableCell80}>
                User ID
              </TableCell>
              <TableCell>Display name</TableCell>
              <TableCell align="center">Change history</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Reported</TableCell>
              <TableCell>Internal Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayNames.length > 0 &&
              displayNames.map(d => (
                <DisplayNameTableRow
                  isReported={isShownReportedOnly}
                  key={d.id}
                  displayName={d}
                  disableReviewStatusChange={isPendingChangeReviewStatus}
                  isChecked={currentSelectedDisplayNameIds.has(d.id)}
                  onCheckboxChange={handleTableRowCheckChange}
                  onReviewStatusChange={handleReviewStatusOfEachItemChange}
                  onChangeHistoryClick={handleChangeHistoryClick}
                  onInternalNoteClick={handleInternalNoteClick}
                />
              ))}
          </TableBody>
        </Table>
        {displayNames.length === 0 && (
          <Box className={classes.noResult}>
            <span>No result</span>
          </Box>
        )}
        {renderTablePagination()}
      </Paper>
      {showChangeHistoryViewer && <DisplayNameChangeHistory displayName={currentViewDisplayName} onClose={handleChangeHistoryViewerClose} />}
      {showInternalNote && (
        <InternalNote
          saveFactory={saveDisplayNameReviewInternalNote}
          displayName={currentViewDisplayName}
          onSave={handleInternalNoteSave}
          onClose={handleInternalNoteClose}
        />
      )}
    </Box>
  );
};

export default DisplayName;
